
import type { Block, Section } from '../types/article'

export const article = {
  eyebrow: 'Dog Safety',
  headline:
    '5 Dangers Every Escape-Artist Dog Owner Needs to Know — And the One Solution That Finally Works',
  dek: 'No buried wire. No contractor. No monthly subscription. Here’s how to keep your dog safe — starting today.',
}

export const introBlocks: Block[] = [
  {
    type: 'beat',
    lines: [
      'Every time the front door opens, your chest tightens.',
      'Every time you hear a car horn down the street, you check the yard.',
      'Every time a visitor comes over, the first thing out of your mouth is “make sure you close the gate.”',
    ],
  },
  {
    type: 'p',
    text: 'You’ve searched for a solution. You’ve priced out fences. You’ve read reviews until midnight.',
  },
  {
    type: 'p',
    text: 'Every option hit a wall. Too expensive. Too cruel. Too unreliable.',
  },
  {
    type: 'p',
    text: 'If you own a dog that bolts every time it sees an open door — a husky, a beagle, a lab, a shepherd — this is your life right now.',
  },
  {
    type: 'p',
    text: 'Here are five dangers you need to know about. And the one solution that is finally keeping escape-artist dogs safe.',
  },
]

export const introImage = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/c642d8c2-338e-4dff-974c-c6c0a70a69fa.jpg',
  alt: 'A woman stands at an open backyard gate at dusk, looking out into an empty suburban street',
}

export const dangers: Section[] = [
  {
    id: 'danger-1',
    eyebrow: 'Danger #1',
    title: 'Your Dog Could Be Killed, and It Happens More Often Than You Think',
    blocks: [
      {
        type: 'p',
        text: '**Tens of thousands of family dogs die from being hit by cars every year** in the United States alone.',
      },
      {
        type: 'pull',
        text: 'One open gate and one passing car. That is all it takes to lose your dog.',
      },
      {
        type: 'p',
        text: 'You check the yard before your coffee. You check it again after.',
      },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/e5cc167b-4b3d-4933-8760-b79b563610a9.jpg',
      alt: 'An empty suburban road at dusk with a dog leash lying on the pavement near the curb',
    },
  },
  {
    id: 'danger-2',
    eyebrow: 'Danger #2',
    title: 'Your Dog Could Disappear and Never Come Back',
    blocks: [
      { type: 'p', text: 'Not every escape ends with your dog being hit by a car.' },
      {
        type: 'p',
        text: 'Sometimes the dog just vanishes. You search the neighborhood, check the shelters, post on every Facebook group in your county.',
      },
      { type: 'beat', lines: ['And sometimes that’s where the story stops.'] },
      {
        type: 'p',
        text: '**Millions of dogs end up in shelters every year** and are never found by their owners.',
      },
      {
        type: 'p',
        text: 'Just an empty bed at home and no answer to the question: What happened to my dog?',
      },
      {
        type: 'p',
        text: 'That thought comes back every time the gate swings open.',
      },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/eced4402-a70c-4b6d-b31e-29f7e2457b38.jpg',
      alt: 'A hand holding a phone late at night, scrolling a lost dog post in a local social media group',
    },
  },
  {
    id: 'danger-3',
    eyebrow: 'Danger #3',
    title: 'One Escape Can Mean Lawsuits, Fines, and Losing Your Dog for Good',
    blocks: [
      {
        type: 'p',
        text: 'A loose dog in someone else’s yard is a legal problem.',
      },
      {
        type: 'p',
        text: 'Dog owners have been fined, sued, and lost their insurance over one open gate.',
      },
      {
        type: 'p',
        text: 'If your dog damages property or bites someone — even out of fear — you could be dealing with lawsuits and vet bills for years.',
      },
      { type: 'p', text: 'And then there is the conversation no one wants to have.' },
      {
        type: 'beat',
        lines: ['Your spouse says: We can’t keep doing this.'],
      },
      {
        type: 'p',
        text: 'You sit on the couch wondering if you’re going to have to give up the dog you love — because you cannot keep it in your yard.',
      },
      {
        type: 'p',
        text: 'One owner wrote that her family considered putting their nine-year-old husky down. Not because the dog was sick. **Because nothing they tried kept it from escaping.**',
      },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/1d27ab3c-4e2c-4c3f-b505-830f656b98c3.jpg',
      alt: 'Two people at a kitchen table late at night, one with her head in her hands, their husky asleep by the back door',
    },
  },
  {
    id: 'danger-4',
    eyebrow: 'Danger #4',
    title: 'The Fence You’re Counting On Won’t Hold When It Matters',
    blocks: [
      {
        type: 'pull',
        text: 'But if a six-foot fence actually stopped escape-artist dogs, you wouldn’t be reading this.',
      },
      {
        type: 'group',
        heading: 'Dogs climb.',
        paragraphs: ['Shepherds and huskies clear six-foot fences from a standing start.'],
      },
      {
        type: 'group',
        heading: 'Dogs dig.',
        paragraphs: ['Beagles and terriers can dig under a fence panel in twenty minutes.'],
      },
      {
        type: 'group',
        heading: 'Dogs push through.',
        paragraphs: [
          'Every fence gets weaker over time. And your dog is testing every weak spot, every day.',
        ],
      },
      {
        type: 'group',
        heading: 'Gates fail.',
        paragraphs: [
          'Visitors leave them open. Kids forget. One unlatched gate makes the whole fence useless.',
        ],
      },
      {
        type: 'p',
        text: 'A fence costs **four to eight thousand dollars.** If your HOA allows it at all.',
      },
      {
        type: 'pull',
        text: 'You could spend eight thousand dollars and your dog could still get out.',
      },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/8ea9dd24-cfac-45e5-9abb-c4be53b87514.jpg',
      alt: 'Three-panel diagram: a dog climbing over a wooden fence, digging under chain-link, and pushing through a bent vinyl panel',
    },
  },
  {
    id: 'danger-5',
    eyebrow: 'Danger #5',
    title: 'Every Escape Teaches Your Dog to Do It Again — and Each One Gets Harder to Stop',
    blocks: [
      {
        type: 'p',
        text: 'Every time your dog gets out and nothing bad happens, your dog learns one thing:',
      },
      { type: 'pull', text: 'Getting out was fun. I want to do it again.' },
      {
        type: 'beat',
        lines: [
          'The first escape is an accident. The second is a test. By the third or fourth, it is a habit.',
        ],
      },
      {
        type: 'p',
        text: 'Each time, the habit gets stronger. Faster. Bolder. Harder to stop.',
      },
      {
        type: 'p',
        text: '**Training alone does not fix this.** You can teach a husky to come when called on a quiet afternoon. But the moment a squirrel runs across the yard, instinct takes over. The dog is not choosing to ignore you. It is doing what it was bred to do.',
      },
      {
        type: 'p',
        text: 'Every day you don’t solve this, the problem gets worse. And every time your dog gets out, you are gambling with your dog’s life.',
      },
      { type: 'beat', lines: ['But you are not out of options.'] },
      {
        type: 'p',
        text: 'There is a solution that works. It does not require a physical fence, a buried wire, a contractor, or a monthly subscription.',
      },
      { type: 'p', text: 'It is called a **GPS fence collar.**' },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/uploads/k1nPZcskQ8Evyvs1vgHqTY/hf_20260827_202321_3a560518-5020-440e-8002-14c2f38c1db4.png',
      alt: 'A husky standing in a grassy yard looking closely at a wooden fence',
    },
  },
]

export const solutionSections: Section[] = [
  {
    id: 'how-it-knows',
    eyebrow: 'How it works',
    title: 'How the Collar Knows Where Your Dog Is',
    blocks: [
      {
        type: 'p',
        text: 'It is a small device on your dog’s neck that connects to dozens of satellites to create **an invisible boundary around your yard.**',
      },
      {
        type: 'p',
        text: 'These are the same satellites used to land airplanes. The collar uses them to know exactly where your dog is — and when your dog is getting close to the edge **before your dog gets there.**',
      },
      {
        type: 'p',
        text: 'Older GPS collars couldn’t do this. The boundary would drift and your dog could be in the neighbor’s yard before the collar even knew. Modern collars connect to enough satellites to track your dog **down to a few feet.**',
      },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/6f7b5466-8737-444d-9a47-48725c4fa5c3.jpg',
      alt: 'Diagram of satellites beaming signal down to a house, with a dotted boundary drawn around the yard and a dog inside it',
    },
  },
  {
    id: 'how-it-teaches',
    eyebrow: 'The training',
    title: 'How It Keeps Your Dog in the Yard — Without Hurting Your Dog',
    blocks: [
      { type: 'pull', text: 'I’m not putting a shock collar on my dog.' },
      {
        type: 'p',
        text: 'Older invisible fences had one setting — the dog crossed the line and got shocked. No warning. Some dogs got scared of their own yards after that.',
      },
      { type: 'beat', lines: ['That is not how this works.'] },
      {
        type: 'p',
        text: 'Think about rumble strips on a highway. Your car drifts toward the edge. You feel a vibration. You correct. You stay in your lane.',
      },
      {
        type: 'p',
        text: 'A GPS fence collar works the same way.',
      },
      {
        type: 'steps',
        items: [
          {
            label: 'A beep',
            text: 'Your dog walks toward the boundary. The collar beeps. Your dog learns what the beep means. It turns around.',
            video: 'https://cdn.magicpatterns.com/uploads/cwJZ5ubkpXH8km9Wvf6ABG/hf_20260811_150237_3c1267ea-289d-4375-8cbf-d99aeb79e401 (1).mp4',
          },
          {
            label: 'A vibration',
            text: 'If the dog ignores the beep, the collar vibrates — like a phone buzzing in your pocket. The dog notices. It turns around.',
            video: 'https://cdn.magicpatterns.com/uploads/ikqX4NZABVuiaY3BqUwZtQ/hf_20260811_150722_95f13378-f153-49cb-a73c-5e54165b1a51 (1).mp4',
          },
          {
            label: 'A mild static correction',
            text: 'Only if the dog ignores both the beep and the vibration. Adjustable. Brief. Last resort, not the first.',
            video: 'https://cdn.magicpatterns.com/uploads/6s3abELmaRhm8u9BCf2k5Y/hf_20260811_151131_8ea0490f-93a1-46bd-97ab-84a8681c2820 (1).mp4',
          },
        ],
      },
      {
        type: 'p',
        text: '**Most dogs never reach Step 3.** Within two or three days, most dogs turn around at the beep. No shock. No fear. No stress.',
      },
      {
        type: 'pull',
        text: 'A shock collar punishes a dog for making a mistake. A boundary training system teaches a dog so it never makes the mistake in the first place.',
      },
    ],
  },
  {
    id: 'breeds',
    eyebrow: 'The breeds',
    title: 'This Works on the Dogs Everyone Said Couldn’t Be Contained',
    blocks: [
      {
        type: 'group',
        heading: 'Huskies',
        paragraphs: [
          'Climbers, diggers, bred to run for miles. Within days, they learn to turn around at the beep.',
        ],
      },
      {
        type: 'group',
        heading: 'Beagles',
        paragraphs: [
          'Nose-driven, will follow a scent through any gap. The beep catches them before they start chasing.',
        ],
      },
      {
        type: 'group',
        heading: 'German shepherds',
        paragraphs: [
          'Strong enough to get past any physical barrier. Smart enough to learn a boundary in two days and stay inside it for years.',
        ],
      },
      {
        type: 'group',
        heading: 'Labs, border collies, boxers, terriers',
        paragraphs: ['The breeds that fill the “lost dog” posts on every local Facebook group.'],
      },
      {
        type: 'p',
        text: '**Your dog is exactly the kind of dog this was built for.**',
      },
    ],
    image: {
      src: 'https://cdn.magicpatterns.com/patterns/generated-images/80c266d1-9812-4a8f-b4bf-5b69bd5273b5.jpg',
      alt: 'A grid of four relaxed dogs in their yards: a husky, a beagle, a German shepherd, and a labrador',
    },
  },
]

export const product = {
  eyebrow: 'The product',
  title: 'Why Most Dog Owners Haven’t Found This Yet',
  blocks: [
    {
      type: 'p',
      text: 'GPS fence collars that work this well cost **six hundred to a thousand dollars.** One of them adds a monthly subscription on top.',
    },
    {
      type: 'p',
      text: 'GPS fence collars that cost less have a different problem. Boundaries that drifted. Collars that lost signal. Companies that denied refunds and wouldn’t answer the phone.',
    },
    { type: 'pull', text: 'That is why we built Rylte.' },
    {
      type: 'p',
      text: 'Rylte connects to the same satellites as the expensive brands. Same 3-step training system.',
    },
    {
      type: 'p',
      text: 'You download a free app. Draw the boundary around your yard. **It takes a few minutes.**',
    },
  ] as Block[],
  image: {
    src: 'https://cdn.magicpatterns.com/uploads/cgpD656gNpy7ZeVEpZsg4o/hf_20260826_113139_c61fa4ad-fe42-44d9-bb26-c4efe68a9984_(1).png',
    alt: 'The Rylte GPS collar: a compact black unit with a colour display on a black woven nylon strap',
  },
  noList: [
    'No buried wire',
    'No contractor',
    'No base station',
    'No monthly subscription — not now, not ever',
  ],
  guarantee: {
    label: '60-day money-back guarantee',
    lines: ['Not thirty days. Sixty.', 'No fine print. No partial refunds. No technicalities.'],
    body: 'If it does not keep your dog in your yard, send it back and get all of your money back. We do this because we know the collar works. And because after all the broken promises and denied refunds — the only way to earn your trust is to put all of the risk on us.',
  },
  cta: 'See the Rylte collar',
}

export const closing = {
  blocks: [
    {
      type: 'p',
      text: 'The checking. The flinching. The watching the gate. You have been carrying this long enough.',
    },
    {
      type: 'p',
      text: 'Rylte solves the problem — not with a wall your dog will climb over, not with a shock your dog will run through.',
    },
    {
      type: 'p',
      text: 'It uses satellites to draw a boundary your dog can learn. It teaches your dog with warnings your dog understands. And it gives you **sixty days to try it with zero risk.**',
    },
  ] as Block[],
  pull: 'Your dog is exactly the kind of dog this was built for.',
  cta: 'See the Rylte collar',
  image: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/d4e5ff2a-60ed-4ce1-89ba-3c6f09a9ecc2.jpg',
    alt: 'A woman sits on her porch steps with coffee while her husky rests in the open, unfenced yard',
  },
}
